import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { WRITE_ROLES, type Application } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { COMPONENT_KIND_LABEL } from '../../lib/components';
import { useAuth } from '../auth/auth';
import { ApplicationDialog } from './ApplicationDialog';

const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });

/**
 * The catalogue of applications under test, and what each is made of.
 *
 * An application is a catalogue entry and a declared architecture; what may be tested is still
 * only ever an engagement's approved scope.
 */
export function ApplicationsPage() {
  const { user } = useAuth();
  const mayWrite = user ? WRITE_ROLES.has(user.role) : false;
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{ application?: Application } | null>(null);

  const applications = useQuery({ queryKey: ['applications'], queryFn: api.listApplications });
  const engagements = useQuery({ queryKey: ['engagements'], queryFn: api.listEngagements });

  const engagementCount = (appId: string) =>
    (engagements.data?.items ?? []).filter((e) => e.applicationId === appId).length;

  const rows = [...(applications.data?.items ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Workspace</span>
          <h1>Applications</h1>
        </div>
        {mayWrite ? (
          <button type="button" className="btn btn-primary" onClick={() => setDialog({})}>
            Register application
          </button>
        ) : null}
      </div>

      <p className="muted" style={{ maxWidth: '72ch', marginTop: -8 }}>
        Each application and the components it is made of. Scans of its engagements fill in its{' '}
        <strong>surface map</strong>: the endpoints they observed, placed on these components.
      </p>

      <section className="card panel table-wrap">
        {applications.isLoading ? (
          <p className="muted">Loading applications…</p>
        ) : applications.isError ? (
          <p className="login-error" role="alert">
            {applications.error instanceof ApiError
              ? applications.error.message
              : 'Could not load applications.'}
          </p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <p>No applications yet.</p>
            <p className="muted">
              Register one and describe its components, then create an engagement to scan it.
            </p>
            {mayWrite ? (
              <button type="button" className="btn btn-primary" onClick={() => setDialog({})}>
                Register application
              </button>
            ) : null}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Criticality</th>
                <th>Components</th>
                <th>Engagements</th>
                <th>Updated</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((app) => (
                <tr key={app.id}>
                  <td>
                    <strong>{app.name}</strong>
                    {app.description ? (
                      <span className="muted app-description">{app.description}</span>
                    ) : null}
                  </td>
                  <td>
                    <span className={`crit crit-${app.criticality.toLowerCase()}`}>
                      {app.criticality}
                    </span>
                  </td>
                  <td>
                    {app.components.length === 0 ? (
                      <span className="muted">None declared</span>
                    ) : (
                      <span className="component-chips">
                        {app.components.slice(0, 4).map((c) => (
                          <span key={c.id} className="pill" title={COMPONENT_KIND_LABEL[c.kind]}>
                            {c.name}
                          </span>
                        ))}
                        {app.components.length > 4 ? (
                          <span className="muted">+{app.components.length - 4}</span>
                        ) : null}
                      </span>
                    )}
                  </td>
                  <td>{engagements.isLoading ? '…' : engagementCount(app.id)}</td>
                  <td className="muted">{date(app.updatedAt)}</td>
                  <td className="row-actions">
                    <Link className="btn btn-outline btn-sm" to={`/app/surface?app=${app.id}`}>
                      Surface map
                    </Link>
                    {mayWrite ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setDialog({ application: app })}
                      >
                        Edit
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {dialog ? (
        <ApplicationDialog
          {...(dialog.application ? { application: dialog.application } : {})}
          onClose={() => setDialog(null)}
          onSaved={(saved) => {
            setDialog(null);
            if (!dialog.application) void navigate(`/app/surface?app=${saved.id}`);
          }}
        />
      ) : null}
    </div>
  );
}
